/**
 * Catálogo UI de proveedores IA — claves idénticas a
 * `watoolx/backend/src/constants/aiProviderCatalog.ts`
 */

export const DEFAULT_AI_PROVIDER = "openrouter-openai";

/** Etiquetas cortas para listas y dropdown */
export const AI_PROVIDER_LABELS = {
  "openai-oficial-gpt4": "OpenAI Oficial (GPT-4)",
  "openai-oficial-gpt4-turbo": "OpenAI Oficial (GPT-4 Turbo - 128k tokens)",
  "openai-oficial-gpt35": "OpenAI Oficial (GPT-3.5 Turbo - económico)",
  "openai-oficial-gpt4o": "OpenAI Oficial (GPT-4o)",
  "openai-oficial-gpt4o-mini": "OpenAI Oficial (GPT-4o mini — recomendado tools/agendas)",
  "gemini-oficial-flash-37": "Google Gemini oficial (3.7 Flash — recomendado agendas)",
  "gemini-oficial-flash-35": "Google Gemini oficial (3.5 Flash)",
  "gemini-oficial-pro-31": "Google Gemini oficial (3.1 Pro — preview, más razonamiento)",
  "gemini-oficial-flash-15": "Google Gemini oficial (2.5 Flash)",
  // Retirados del selector el 21-ago-2026 tras medirlos uno a uno con
  // `backend/scripts/diagnostico-gemini-tools.js`. Se conservan las etiquetas para
  // que un prompt antiguo que aún los tenga siga mostrando un nombre legible:
  //   2.0 Flash → 404 en todas las llamadas.
  //   2.5 Pro   → solo respeta el contrato de herramientas 2 de cada 4 veces.
  "gemini-oficial-flash": "Google Gemini oficial (2.0 Flash — retirado)",
  "gemini-oficial-pro": "Google Gemini oficial (2.5 Pro — retirado)",
  "anthropic-oficial-claude-sonnet": "Anthropic Claude 3.5 Sonnet (oficial)",
  "anthropic-oficial-claude-haiku": "Anthropic Claude 3.5 Haiku (oficial)",
  "anthropic-oficial-claude-opus": "Anthropic Claude 3 Opus (oficial)",
  "openrouter-openai": "OpenRouter (OpenAI GPT-4o mini)",
  "openrouter-openai-gpt35": "OpenRouter (OpenAI GPT-3.5 Turbo)",
  "openrouter-gpt4o-mini": "OpenRouter (GPT-4o mini)",
  "openrouter-openai-gpt4o": "OpenRouter (OpenAI GPT-4o)",
  "openrouter-deepseek": "OpenRouter (DeepSeek)",
  "openrouter-meta": "OpenRouter (Meta Llama 3.3 70B)",
  "openrouter-anthropic": "OpenRouter (Claude 3.5 Sonnet)",
  "openrouter-gemini": "OpenRouter (Gemini 2.0 Flash)",
  "openrouter-qwen": "OpenRouter (Qwen 2.5 72B)",
  "openrouter-kimi-k2": "OpenRouter (Kimi K2 — Moonshot)",
  openai: "OpenAI GPT-3.5 (legacy)",
  openrouter: "OpenRouter (predeterminado)"
};

export function getProviderLabel(provider) {
  if (!provider) return "Sin proveedor";
  return AI_PROVIDER_LABELS[provider] || provider;
}

export function getProviderKeyKind(provider) {
  const p = (provider || "").trim();
  if (p.startsWith("openai-oficial")) return "openai";
  if (p.startsWith("gemini-oficial")) return "gemini";
  if (p.startsWith("anthropic-oficial")) return "anthropic";
  return "openrouter";
}

export function getApiKeyUi(provider) {
  const kind = getProviderKeyKind(provider);
  if (kind === "openai") {
    return {
      label: "API Key de OpenAI",
      placeholder: "sk-...",
      helper: "Tu clave API de OpenAI (sk-...)"
    };
  }
  if (kind === "gemini") {
    return {
      label: "API Key de Google AI Studio",
      placeholder: "AIza...",
      helper: "Clave de Google AI Studio (Gemini API, compatible OpenAI)"
    };
  }
  if (kind === "anthropic") {
    return {
      label: "API Key de Anthropic",
      placeholder: "sk-ant-api...",
      helper: "Clave API de Anthropic (sk-ant-...)"
    };
  }
  return {
    label: "API Key de OpenRouter",
    placeholder: "sk-or-v1-...",
    helper: "Tu clave API de OpenRouter"
  };
}

/**
 * Secciones del <Select> (orden = orden en UI)
 * useProviderChipClass: usa `classes.providerChip` del modal
 */
export const AI_PROVIDER_MENU_SECTIONS = [
  {
    header: "OpenAI Oficial",
    items: [
      {
        value: "openai-oficial-gpt4o-mini",
        chip: "OpenAI",
        chipStyle: { backgroundColor: "#10a37f", color: "white", fontWeight: "bold" }
      },
      {
        value: "openai-oficial-gpt4o",
        chip: "OpenAI",
        chipStyle: { backgroundColor: "#10a37f", color: "white", fontWeight: "bold" }
      },
      {
        value: "openai-oficial-gpt4",
        chip: "OpenAI",
        chipStyle: { backgroundColor: "#10a37f", color: "white", fontWeight: "bold" }
      },
      {
        value: "openai-oficial-gpt4-turbo",
        chip: "OpenAI",
        chipStyle: { backgroundColor: "#10a37f", color: "white", fontWeight: "bold" }
      },
      {
        value: "openai-oficial-gpt35",
        chip: "OpenAI",
        chipStyle: { backgroundColor: "#10a37f", color: "white", fontWeight: "bold" }
      }
    ]
  },
  {
    /**
     * Solo modelos verificados con herramientas (21-ago-2026).
     *
     * Cada uno se probó con `backend/scripts/diagnostico-gemini-tools.js`, que les
     * pide una llamada a herramienta real y comprueba si la emiten de verdad:
     *
     *   3.7 Flash · 3.5 Flash · 3.1 Pro    3 de 3 aciertos
     *   2.5 Pro                            2 de 4  → fuera
     *   2.0 Flash                          404 siempre → fuera
     *
     * Los dos retirados no se borran del catálogo de etiquetas: un prompt antiguo
     * podría seguir apuntando a ellos y debe poder mostrar su nombre. Lo que se
     * evita es que alguien vuelva a elegirlos desde esta lista.
     *
     * `2.5 Pro` no fallaba siempre, y ese era el problema: la mitad de las veces
     * ignoraba las herramientas y respondía texto corriente. Un modelo que acierta
     * la mitad de las veces produce fallos intermitentes, que son los más caros de
     * diagnosticar — meses, en este caso.
     */
    header: "Google Gemini Oficial",
    items: [
      {
        value: "gemini-oficial-flash-37",
        chip: "Gemini",
        chipStyle: { backgroundColor: "#4285f4", color: "white", fontWeight: "bold" }
      },
      {
        value: "gemini-oficial-flash-35",
        chip: "Gemini",
        chipStyle: { backgroundColor: "#4285f4", color: "white", fontWeight: "bold" }
      },
      {
        value: "gemini-oficial-pro-31",
        chip: "Gemini",
        chipStyle: { backgroundColor: "#4285f4", color: "white", fontWeight: "bold" }
      },
      {
        value: "gemini-oficial-flash-15",
        chip: "Gemini",
        chipStyle: { backgroundColor: "#4285f4", color: "white", fontWeight: "bold" }
      }
    ]
  },
  {
    header: "Anthropic Claude Oficial",
    items: [
      {
        value: "anthropic-oficial-claude-sonnet",
        chip: "Claude",
        chipStyle: { backgroundColor: "#d97757", color: "white", fontWeight: "bold" }
      },
      {
        value: "anthropic-oficial-claude-haiku",
        chip: "Claude",
        chipStyle: { backgroundColor: "#d97757", color: "white", fontWeight: "bold" }
      },
      {
        value: "anthropic-oficial-claude-opus",
        chip: "Claude",
        chipStyle: { backgroundColor: "#d97757", color: "white", fontWeight: "bold" }
      }
    ]
  },
  {
    header: "OpenRouter (Múltiples modelos)",
    items: [
      { value: "openrouter-openai", chip: "OpenAI", useProviderChipClass: true },
      { value: "openrouter-gpt4o-mini", chip: "OpenAI", useProviderChipClass: true },
      { value: "openrouter-openai-gpt4o", chip: "OpenAI", useProviderChipClass: true },
      { value: "openrouter-openai-gpt35", chip: "OpenAI", useProviderChipClass: true },
      { value: "openrouter-kimi-k2", chip: "Kimi", useProviderChipClass: true },
      { value: "openrouter-deepseek", chip: "DeepSeek", useProviderChipClass: true },
      { value: "openrouter-meta", chip: "Meta", useProviderChipClass: true },
      { value: "openrouter-anthropic", chip: "Claude", useProviderChipClass: true },
      { value: "openrouter-gemini", chip: "Gemini", useProviderChipClass: true },
      { value: "openrouter-qwen", chip: "Qwen", useProviderChipClass: true }
    ]
  }
];
